import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import DeleteIcon from '@mui/icons-material/DeleteOutline';

import { ChatMessage } from './components/chat-message';
import { ChatInput } from './components/chat-input';
import {
  buttonsWrapper,
  chatArea,
  chatHeaderBar,
  chatScrollArea,
  chatShell,
  conversationItem,
  conversationList,
  footerNotice,
  newChatButton,
  promptGrid,
  promptTile,
  sidebarHeader,
  sidebarPaper,
  starterCard,
  termsLink,
  typingDots,
} from './styles';
import { useAppAuth } from 'src/context/use-app-auth';
import { useTranslation } from 'src/text';
import { ChatProvider, useAiAssistantChat } from './context/chat-context';
import { genericLabels } from 'src/text/locales/default-en';
import { useSubmitMessage } from './hooks/use-submit-message';

type Prompt = { text: string; category?: string };
type PromptResponse = { prompts?: Array<Prompt | string | null | undefined> };
type Promptish = Prompt | string | null | undefined;

const normalizePrompt = (item: Promptish): Prompt | null => {
  if (!item) return null;
  if (typeof item === 'string') return { text: item };
  if (typeof item === 'object') {
    const text = (item as { text?: string }).text ?? '';
    const category = (item as { category?: string }).category;
    if (!text) return null;
    return { text, category };
  }
  return null;
};

const isPrompt = (p: Prompt | null): p is Prompt => !!p && !!p.text;

/* eslint-disable no-unused-vars, @typescript-eslint/no-unused-vars */
type ConversationStartersProps = {
  prompts: Prompt[];
  loading: boolean;
  onRefresh: () => void;
  onSend: (text: string) => void | Promise<void>;
  title: string;
};
/* eslint-enable no-unused-vars, @typescript-eslint/no-unused-vars */

const ConversationStarters = ({
  prompts,
  loading,
  onRefresh,
  onSend,
  title,
}: ConversationStartersProps) => (
  <Paper elevation={0} sx={starterCard}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      <IconButton size="small" onClick={onRefresh} disabled={loading}>
        <RefreshIcon
          fontSize="small"
          sx={{
            animation: loading ? 'spin 1s linear infinite' : 'none',
            '@keyframes spin': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' },
            },
          }}
        />
      </IconButton>
    </Stack>
    <Box sx={promptGrid} mt={2}>
      {prompts.map(prompt => (
        <Box
          key={prompt.text}
          sx={promptTile()}
          onClick={() => onSend(prompt.text)}>
          <Typography variant="body2" fontWeight={600}>
            {prompt.text}
          </Typography>
          {prompt.category && (
            <Chip
              label={prompt.category}
              size="small"
              sx={{ mt: 1, width: 'fit-content' }}
            />
          )}
        </Box>
      ))}
    </Box>
  </Paper>
);

const Sidebar = () => {
  const {
    conversations,
    loadingConversations,
    activeConversationId,
    selectConversation,
    deleteConversation,
    startNewConversation,
    refreshConversations,
    error,
  } = useAiAssistantChat();
  const { assistantLabels } = useTranslation();
  const emptyConvoLabel =
    (assistantLabels as { emptyConversations?: string }).emptyConversations ??
    'No conversations yet.';
  const refreshLabel =
    (assistantLabels as { refresh?: string }).refresh ?? 'Refresh';
  const startNewLabel =
    (assistantLabels as { startNew?: string }).startNew ?? 'New chat';

  return (
    <Paper elevation={0} sx={sidebarPaper}>
      <Box sx={sidebarHeader}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {genericLabels.company}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {assistantLabels.promptTitle}
          </Typography>
        </Box>
      </Box>
      <Box px={2} py={1.5}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          startIcon={<AddIcon />}
          sx={newChatButton}
          onClick={startNewConversation}>
          {startNewLabel}
        </Button>
      </Box>
      <Divider />
      <Box sx={conversationList}>
        {loadingConversations ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              py: 3,
            }}>
            <CircularProgress size={20} />
          </Box>
        ) : conversations.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            px={2}
            py={2}>
            {emptyConvoLabel}
          </Typography>
        ) : (
          conversations.map(convo => (
            <Box
              key={convo.id}
              sx={conversationItem(convo.id === activeConversationId)}
              onClick={() => selectConversation(convo.id)}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor:
                    convo.id === activeConversationId
                      ? 'primary.main'
                      : 'divider',
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {convo.title || assistantLabels.exampleTitle}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {convo.updatedAt || convo.createdAt
                    ? new Date(
                        convo.updatedAt || convo.createdAt || '',
                      ).toLocaleString()
                    : assistantLabels.exampleTitle}
                </Typography>
              </Box>
              <Box
                className="convo-actions"
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                  pointerEvents: 'none',
                }}
                onClick={e => e.stopPropagation()}>
                <Tooltip title={genericLabels.delete || 'Delete'}>
                  <IconButton
                    size="small"
                    aria-label="Delete conversation"
                    onClick={() => deleteConversation(convo.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))
        )}
      </Box>
      <Divider />
      <Box px={2} py={1.5}>
        <Button
          variant="text"
          size="small"
          startIcon={<SyncIcon />}
          onClick={refreshConversations}
          sx={{ textTransform: 'none' }}>
          {refreshLabel}
        </Button>
        {error && (
          <Typography variant="caption" color="error" display="block" mt={1}>
            {error}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

const ChatSurface = () => {
  const { assistantLabels } = useTranslation();
  const { user, location } = useAppAuth();
  const { messages, loadingMessages, sending, error } = useAiAssistantChat();
  const { submitMessage } = useSubmitMessage();
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const promptLibrary: Prompt[] = useMemo(() => {
    const incoming = assistantLabels.prompts;
    if (!Array.isArray(incoming)) return [];
    return incoming.map(normalizePrompt).filter(isPrompt);
  }, [assistantLabels.prompts]);

  const [promptSeed, setPromptSeed] = useState(0);
  const [remotePrompts, setRemotePrompts] = useState<Prompt[]>([]);
  const [promptsLoading, setPromptsLoading] = useState(false);

  const fetchPrompts = useCallback(async () => {
    setPromptsLoading(true);
    try {
      const tenantId = user?.key || location?.key || 'default';
      const res = await fetch(
        `/api/assistant/prompts?tenant=${encodeURIComponent(
          tenantId,
        )}&seed=${promptSeed}`,
      );
      if (!res.ok) {
        throw new Error('Failed to load prompts');
      }
      const data: PromptResponse = await res.json();
      const prompts =
        Array.isArray(data?.prompts) && data.prompts.length > 0
          ? data.prompts
          : [];
      const normalized: Prompt[] = prompts
        .map(normalizePrompt)
        .filter(isPrompt);
      setRemotePrompts(normalized);
    } catch (_e) {
      setRemotePrompts([]);
    } finally {
      setPromptsLoading(false);
    }
  }, [location?.key, promptSeed, user?.key]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  const starterPrompts = useMemo(() => {
    const timeOfDay = new Date().getHours();
    const preferred =
      timeOfDay < 12 ? ['insights', 'ops'] : ['marketing', 'reviews'];
    const pool = (
      remotePrompts.length > 0 ? remotePrompts : promptLibrary
    ).sort((a, b) =>
      preferred.includes(a.category ?? '') &&
      !preferred.includes(b.category ?? '')
        ? -1
        : 1,
    );
    const startIndex = pool.length > 4 ? promptSeed % (pool.length - 3) : 0;
    return pool.slice(startIndex, startIndex + 4);
  }, [promptLibrary, promptSeed, remotePrompts]);

  const handleTermsPressed = () => {
    window.open('/terms-of-use', '_blank');
  };

  return (
    <Box sx={chatArea}>
      <Box sx={chatHeaderBar}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {assistantLabels.exampleTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {assistantLabels.exampleBody}
          </Typography>
        </Box>
      </Box>

      <Box sx={chatScrollArea} ref={scrollRef}>
        {loadingMessages ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              minHeight: 240,
            }}>
            <CircularProgress />
          </Box>
        ) : messages.length === 0 ? (
          <ConversationStarters
            prompts={starterPrompts}
            loading={promptsLoading}
            onRefresh={() => setPromptSeed(seed => seed + 1)}
            onSend={text => submitMessage({ text })}
            title={assistantLabels.promptTitle}
          />
        ) : (
          messages.map((msg, idx) => (
            <ChatMessage key={msg.id || `${msg.role}-${idx}`} message={msg} />
          ))
        )}
        {sending && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Box sx={typingDots} aria-label="Assistant typing" />
          </Box>
        )}
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
      </Box>

      <Divider />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <ChatInput loading={sending} onSend={text => submitMessage({ text })} />
        <Box sx={buttonsWrapper}>
          <Typography variant="caption" sx={footerNotice}>
            {assistantLabels.disclaimerPrefix}{' '}
            <Box component="span" sx={termsLink} onClick={handleTermsPressed}>
              {assistantLabels.termsLink}
            </Box>
            .
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const AiAssistantContainer = () => (
  <ChatProvider>
    <Box sx={chatShell}>
      <Sidebar />
      <ChatSurface />
    </Box>
  </ChatProvider>
);

export { AiAssistantContainer };
